// 261005 E3-P3a — modal XEM THỬ → ÁP sửa tay chặng ③ (dùng chung `EntityTable` ② và `AdaptationLocalize` ③).
// Luồng: mở với `ops` ⇒ POST preview (không ghi) ⇒ người xem cặp đổi tên / tập-cảnh bị thay / chương Chapters KHÔNG cập nhật / cảnh báo cứng đổi bối cảnh
// ⇒ "Áp" ⇒ PATCH kèm `expectedModifiedAt = baseModifiedAt` của xem thử. Kết quả (chương đã đẩy lại / bị bỏ qua) GIỮ HIỂN THỊ tới khi người đóng.
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal, Space, Spin, Typography, message } from 'antd';
import { adaptationApi } from '../../../services/api/adaptation';
import type { LocalizeOp, LocalizePatchResponse, LocalizePreviewResponse } from '../../../types/adaptation';
import {
  ApplyResultView, PreviewSection, applyResultView, canApplyPreview, editErrorText, previewSections, screenStale,
} from './localizeEditView';

const { Text } = Typography;

interface Props {
  storyId: string;
  /** `null` ⇒ đóng. Đổi tham chiếu ⇒ xem thử lại. */
  ops: LocalizeOp[] | null;
  title: string;
  /** Mốc `film_outlines.modified_at` mà màn hình đang hiển thị (nếu có) — lệch mốc xem thử ⇒ báo dữ liệu đã đổi. */
  screenModifiedAt?: string | null;
  onClose: () => void;
  /** Gọi NGAY sau khi ghi thành công (cha tải lại dữ liệu); modal vẫn mở để hiện kết quả. */
  onApplied: (r: LocalizePatchResponse) => void;
  /** Lỗi cần tải lại (OUTLINE_CHANGED / SCRIPT_CHANGED). */
  onStale?: () => void;
}

export const renderSections = (sections: PreviewSection[]): React.ReactNode => (
  <Space direction="vertical" size={8} style={{ width: '100%' }}>
    {sections.map((s) => (
      <Alert
        key={s.key} type={s.tone} showIcon message={s.title}
        description={s.lines.length ? (
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            {s.lines.map((l, i) => <Text key={i} style={{ fontSize: 13 }}>{l}</Text>)}
          </Space>
        ) : undefined}
      />
    ))}
  </Space>
);

const LocalizeEditModal: React.FC<Props> = ({ storyId, ops, title, screenModifiedAt, onClose, onApplied, onStale }) => {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState<LocalizePreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ApplyResultView | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    setPreview(null); setError(null); setDone(null);
    if (!ops) return undefined;
    const my = ++seq.current;
    setLoading(true);
    adaptationApi.previewLocalizeOps(storyId, ops)
      .then((p) => { if (seq.current === my) setPreview(p); })
      .catch((e) => { if (seq.current === my) setError(editErrorText(e).text); })
      .finally(() => { if (seq.current === my) setLoading(false); });
    return () => { seq.current++; };
  }, [ops, storyId]);

  const stale = !!preview && screenStale(screenModifiedAt, preview.baseModifiedAt);

  const apply = async () => {
    if (!ops || !preview || applying) return;
    setApplying(true);
    try {
      const r = await adaptationApi.patchLocalizeOps(storyId, ops, preview.baseModifiedAt);
      const v = applyResultView(r);
      setDone(v);
      if (r.written) message.success(v.success);
      onApplied(r);
    } catch (e) {
      const d = editErrorText(e);
      setError(d.text);
      if (d.refresh) onStale?.();
    } finally {
      setApplying(false);
    }
  };

  return (
    <Modal
      open={!!ops} title={done ? 'Kết quả' : title} width={640} onCancel={onClose} destroyOnClose maskClosable={!applying}
      footer={done
        ? <Button type="primary" onClick={onClose}>Đóng</Button>
        : [
          <Button key="x" onClick={onClose} disabled={applying}>Thôi</Button>,
          <Button key="ok" type="primary" loading={applying} disabled={!canApplyPreview(preview) || stale} onClick={apply}>Áp</Button>,
        ]}
    >
      {loading && <Spin />}
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 8 }} />}
      {stale && <Alert type="warning" showIcon message="Dữ liệu trên màn hình đã cũ so với server — đóng rồi mở lại sau khi tải." style={{ marginBottom: 8 }} />}
      {preview && !done && (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {preview.entities.map((e) => (
            <Text key={e.id} style={{ fontSize: 13 }}>
              <Text strong>{e.name || e.id}</Text>: {e.before.localizedName || '(trống)'} → {e.after.localizedName || '(trống)'}
              {e.before.locked !== e.after.locked ? (e.after.locked ? ' · khoá' : ' · mở khoá') : ''}
            </Text>
          ))}
          {preview.applied.length === 0 && !preview.hasErrors && <Alert type="info" showIcon message="Không có thay đổi nào để áp." />}
          {renderSections(previewSections(preview))}
        </Space>
      )}
      {done && renderSections(done.sections.length ? done.sections : [{ key: 'ok', tone: 'success', title: done.success, lines: [] }])}
    </Modal>
  );
};

export default LocalizeEditModal;

/** Áp lệnh KHÔNG cần người xem (không ảnh hưởng kịch bản: khoá, thêm/xoá từ vựng, đổi loại): xem thử lấy mốc rồi PATCH. Ném lỗi HTTP nguyên trạng. */
export const applyLocalizeOpsDirect = async (storyId: string, ops: LocalizeOp[], baseModifiedAt?: string): Promise<LocalizePatchResponse> => {
  const p = await adaptationApi.previewLocalizeOps(storyId, ops);
  if (p.hasErrors) {
    const err: any = new Error('HAS_ERRORS');
    err.response = { data: { code: 'HAS_ERRORS', message: `Có ${p.violations.errors.length} lỗi chặn: ${p.violations.errors.map((v) => v.message).join(' · ')}` } };
    throw err;
  }
  if (!p.applied.length) {
    const err: any = new Error('NOTHING');
    err.response = { data: { message: p.rejected[0]?.reason || 'Không có thay đổi nào để áp' } };
    throw err;
  }
  // `baseModifiedAt` (3h4c): mốc của «hộp hậu quả» người vừa xem ⇒ dữ liệu đổi sau đó thì BE trả 409 `OUTLINE_CHANGED` (hộp tải lại hệ quả) thay vì áp lên bản khác.
  return adaptationApi.patchLocalizeOps(storyId, ops, baseModifiedAt || p.baseModifiedAt);
};
